"use client";

import { useState, useMemo, useCallback, useRef } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import Link from "next/link";
import SiteFooter from "@/components/SiteFooter";
import {
  Search, Download, X,
  Smartphone, Cpu, SlidersHorizontal, ChevronRight,
  ChevronUp, ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DifficultyBadge } from "@/components/DifficultyBadge";
import type { Difficulty } from "@/lib/repairData";
import type { PhoneModelRow } from "@/lib/modelsDb";

const DIFFICULTIES: ("all" | Difficulty)[] = ["all", "低", "中", "高"];
const YEARS_FROM = 2007;
const YEARS_TO = 2025;

type SortKey = "year" | "name" | "group";
type SortDir = "asc" | "desc";

interface Props {
  models: PhoneModelRow[];
  makerNames: string[];
}

export default function ModelListClient({ models, makerNames }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // URLパラメータからフィルター初期値を読む（クライアント側のみ）
  const groupFilter = (searchParams.get("group") ?? "all") as "all" | "iPhone" | "Android";
  const makerFilter = searchParams.get("maker") ?? "すべて";
  const yearFrom = Number(searchParams.get("yearFrom") ?? YEARS_FROM);
  const yearTo = Number(searchParams.get("yearTo") ?? YEARS_TO);
  const diffFilter = (searchParams.get("diff") ?? "all") as "all" | Difficulty;

  // 検索ワードはローカルstate + デバウンスでURLに反映
  const [searchInput, setSearchInput] = useState(searchParams.get("q") ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [showFilters, setShowFilters] = useState(false);

  const updateParam = useCallback(
    (updates: Record<string, string | undefined>) => {
      const sp = new URLSearchParams(searchParams.toString());
      for (const [k, v] of Object.entries(updates)) {
        if (v === undefined || v === "") sp.delete(k);
        else sp.set(k, v);
      }
      const qs = sp.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    },
    [searchParams, pathname, router]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateParam({ q: value || undefined });
      }, 400);
    },
    [updateParam]
  );

  const clearFilters = () => {
    setSearchInput("");
    router.replace(pathname);
  };

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey !== key) {
        setSortKey(key);
        setSortDir("asc");
      } else if (sortDir === "asc") {
        setSortDir("desc");
      } else {
        setSortKey(null);
        setSortDir("asc");
      }
    },
    [sortKey, sortDir]
  );

  const hasFilters =
    groupFilter !== "all" ||
    makerFilter !== "すべて" ||
    yearFrom !== YEARS_FROM ||
    yearTo !== YEARS_TO ||
    diffFilter !== "all" ||
    !!searchInput;

  const query = searchParams.get("q") ?? "";

  const filtered = useMemo(() => {
    let result = models;
    if (query) {
      const q = query.toLowerCase();
      result = result.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.series.toLowerCase().includes(q) ||
          String(m.year).includes(q)
      );
    }
    if (groupFilter !== "all") result = result.filter((m) => m.group === groupFilter);
    if (makerFilter !== "すべて") result = result.filter((m) => m.makerName === makerFilter);
    result = result.filter((m) => m.year >= yearFrom && m.year <= yearTo);
    if (diffFilter !== "all") result = result.filter((m) => m.difficulty === diffFilter);
    return result;
  }, [models, query, groupFilter, makerFilter, yearFrom, yearTo, diffFilter]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "year") cmp = a.year - b.year;
      else if (sortKey === "name") cmp = a.name.localeCompare(b.name, "ja");
      else if (sortKey === "group") cmp = a.group.localeCompare(b.group, "ja");
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const diffColors: Record<Difficulty, string> = {
    "低": "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    "中": "bg-amber-500/10 text-amber-400 border-amber-500/30",
    "高": "bg-red-500/10 text-red-400 border-red-500/30",
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return null;
    return sortDir === "asc"
      ? <ChevronUp className="w-3 h-3 text-primary" />
      : <ChevronDown className="w-3 h-3 text-primary" />;
  };

  return (
    <div
      className="h-screen bg-background text-foreground flex flex-col"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <div className="flex flex-1 pt-14 overflow-hidden min-h-0">
        {/* ── サイドバー（デスクトップ） ── */}
        <aside
          className="hidden lg:flex flex-col w-64 shrink-0 border-r border-border/50 p-4 overflow-y-auto"
          style={{ background: "oklch(0.13 0.015 260)", height: "calc(100vh - 56px)", position: "sticky", top: "56px" }}
        >
          <div className="mb-4">
            <p className="text-xs font-mono text-muted-foreground tracking-widest uppercase mb-3">グループ</p>
            <div className="flex flex-col gap-1">
              {(["all", "iPhone", "Android"] as const).map((g) => (
                <button
                  key={g}
                  onClick={() => updateParam({ group: g === "all" ? undefined : g, maker: undefined })}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors text-left ${
                    groupFilter === g
                      ? "bg-primary/20 text-primary border border-primary/40"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  }`}
                >
                  {g === "iPhone" && <Smartphone className="w-3.5 h-3.5" />}
                  {g === "Android" && <Cpu className="w-3.5 h-3.5" />}
                  {g === "all" ? "すべて" : g}
                </button>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-2">修理難易度</p>
            <div className="flex flex-col gap-1">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d}
                  onClick={() => updateParam({ diff: d === "all" ? undefined : d })}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors text-left border ${
                    diffFilter === d
                      ? d === "all"
                        ? "bg-primary/20 text-primary border-primary/40"
                        : diffColors[d as Difficulty]
                      : "text-muted-foreground border-transparent hover:text-foreground hover:bg-accent"
                  }`}
                >
                  {d !== "all" && (
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        d === "低" ? "bg-emerald-400" : d === "中" ? "bg-amber-400" : "bg-red-400"
                      }`}
                    />
                  )}
                  {d === "all" ? "すべて" : `難易度：${d}`}
                </button>
              ))}
            </div>
          </div>
          {groupFilter !== "iPhone" && (
            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-2">メーカー</p>
              <div className="flex flex-col gap-1">
                {["すべて", ...makerNames].map((maker) => (
                  <button
                    key={maker}
                    onClick={() => updateParam({ maker: maker === "すべて" ? undefined : maker })}
                    className={`px-3 py-1.5 rounded text-sm transition-colors text-left ${
                      makerFilter === maker
                        ? "bg-primary/20 text-primary border border-primary/40"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    }`}
                  >
                    {maker}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-2">発売年</p>
            <div className="flex items-center gap-2">
              <select
                value={yearFrom}
                onChange={(e) =>
                  updateParam({ yearFrom: Number(e.target.value) !== YEARS_FROM ? e.target.value : undefined })
                }
                className="flex-1 bg-input border border-border rounded px-2 py-1.5 text-xs text-foreground"
              >
                {Array.from({ length: YEARS_TO - YEARS_FROM + 1 }, (_, i) => YEARS_FROM + i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <span className="text-muted-foreground text-xs">〜</span>
              <select
                value={yearTo}
                onChange={(e) =>
                  updateParam({ yearTo: Number(e.target.value) !== YEARS_TO ? e.target.value : undefined })
                }
                className="flex-1 bg-input border border-border rounded px-2 py-1.5 text-xs text-foreground"
              >
                {Array.from({ length: YEARS_TO - YEARS_FROM + 1 }, (_, i) => YEARS_FROM + i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs text-muted-foreground hover:text-foreground border border-border/60 hover:border-border transition-colors"
            >
              <X className="w-3 h-3" /> フィルターをリセット
            </button>
          )}
          <div className="mt-auto pt-4 border-t border-border/50">
            <p className="text-xs font-mono text-muted-foreground tracking-widest uppercase mb-3">CSVダウンロード</p>
            <div className="flex flex-col gap-2">
              <a href="/api/csv/all">
                <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs justify-start border-border hover:border-primary/60">
                  <Download className="w-3 h-3" /> 統合版（全機種）
                </Button>
              </a>
              <a href="/api/csv/iphone">
                <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs justify-start border-border hover:border-primary/60">
                  <Download className="w-3 h-3" /> iPhone のみ
                </Button>
              </a>
              <a href="/api/csv/android">
                <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs justify-start border-border hover:border-primary/60">
                  <Download className="w-3 h-3" /> Android のみ
                </Button>
              </a>
            </div>
          </div>
        </aside>

        {/* ── メインコンテンツ ── */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden min-h-0">
          {/* 検索バー */}
          <div
            className="shrink-0 border-b border-border/50 px-4 py-3 flex items-center gap-3"
            style={{ background: "oklch(0.13 0.015 260)" }}
          >
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="機種名・メーカー名・発売年で検索..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 bg-input border-border text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-primary/50"
              />
              {searchInput && (
                <button
                  onClick={() => handleSearchChange("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center gap-1.5 px-3 py-2 rounded border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              フィルター
              {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
            </button>
          </div>

          {/* モバイルフィルター */}
          {showFilters && (
            <div
              className="lg:hidden overflow-hidden border-b border-border/50"
              style={{ background: "oklch(0.14 0.015 260)" }}
            >
              <div className="p-4 flex flex-wrap gap-2">
                {(["all", "iPhone", "Android"] as const).map((g) => (
                  <button
                    key={g}
                    onClick={() => updateParam({ group: g === "all" ? undefined : g, maker: undefined })}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      groupFilter === g
                        ? "bg-primary/20 text-primary border-primary/40"
                        : "text-muted-foreground border-border hover:text-foreground"
                    }`}
                  >
                    {g === "all" ? "すべて" : g}
                  </button>
                ))}
                {DIFFICULTIES.map((d) => (
                  <button
                    key={d}
                    onClick={() => updateParam({ diff: d === "all" ? undefined : d })}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      diffFilter === d
                        ? "bg-primary/20 text-primary border-primary/40"
                        : "text-muted-foreground border-border hover:text-foreground"
                    }`}
                  >
                    {d === "all" ? "難易度：すべて" : `難易度：${d}`}
                  </button>
                ))}
                {hasFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-3 py-1.5 rounded-full text-xs border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    リセット
                  </button>
                )}
              </div>
            </div>
          )}

          {/* テーブル */}
          <div className="flex-1 overflow-auto">
            {sorted.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 gap-3">
                <Search className="w-8 h-8 text-muted-foreground/30" />
                <p className="text-muted-foreground text-sm">該当する機種が見つかりません</p>
                {hasFilters && (
                  <button onClick={clearFilters} className="text-xs text-primary hover:underline">
                    フィルターをリセット
                  </button>
                )}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead
                  className="sticky top-0 z-10 border-b border-border/50"
                  style={{ background: "oklch(0.14 0.015 260)" }}
                >
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-mono text-muted-foreground tracking-widest uppercase w-20 hidden sm:table-cell">
                      <button
                        onClick={() => handleSort("year")}
                        className="flex items-center gap-1 hover:text-foreground transition-colors whitespace-nowrap"
                      >
                        発売年
                        <SortIcon col="year" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-mono text-muted-foreground tracking-widest uppercase">
                      <button
                        onClick={() => handleSort("name")}
                        className="flex items-center gap-1 hover:text-foreground transition-colors whitespace-nowrap"
                      >
                        機種名
                        <SortIcon col="name" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-mono text-muted-foreground tracking-widest uppercase hidden md:table-cell">
                      メーカー / シリーズ
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-mono text-muted-foreground tracking-widest uppercase w-28 hidden sm:table-cell">
                      <button
                        onClick={() => handleSort("group")}
                        className="flex items-center gap-1 hover:text-foreground transition-colors whitespace-nowrap"
                      >
                        グループ
                        <SortIcon col="group" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-mono text-muted-foreground tracking-widest uppercase w-28">
                      修理難易度
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-mono text-muted-foreground tracking-widest uppercase w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((model, i) => (
                    <tr
                      key={`${model.id}-${i}`}
                      className="border-b border-border/30 hover:bg-accent/50 transition-colors group"
                    >
                      <td className="px-4 py-3 font-mono text-muted-foreground text-xs hidden sm:table-cell">
                        {model.year}
                      </td>
                      <td
                        className="px-4 py-3 font-medium group-hover:text-primary transition-colors"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                      >
                        <Link href={`/models/${model.id}`}>{model.name}</Link>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                        {model.series || "—"}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono border ${
                            model.group === "iPhone"
                              ? "bg-blue-500/10 text-blue-400 border-blue-500/30"
                              : "bg-green-500/10 text-green-400 border-green-500/30"
                          }`}
                        >
                          {model.group === "iPhone" ? (
                            <Smartphone className="w-2.5 h-2.5" />
                          ) : (
                            <Cpu className="w-2.5 h-2.5" />
                          )}
                          {model.group}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {model.difficulty ? (
                          <DifficultyBadge difficulty={model.difficulty} />
                        ) : (
                          <span className="text-xs text-muted-foreground/40 font-mono">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link href={`/models/${model.id}`}>
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors font-mono group-hover:text-primary/80">
                            詳細 <ChevronRight className="w-3 h-3" />
                          </span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* フッター */}
          <div
            className="border-t border-border/50 px-4 py-3 flex items-center justify-between text-xs text-muted-foreground font-mono"
            style={{ background: "oklch(0.13 0.015 260)" }}
          >
            <span>{filtered.length.toLocaleString()} 件表示 / 全 {models.length.toLocaleString()} 件</span>
            <div className="flex gap-3">
              <a href="/api/csv/all" className="flex items-center gap-1 hover:text-primary transition-colors">
                <Download className="w-3 h-3" /> 統合CSV
              </a>
              <a href="/api/csv/iphone" className="flex items-center gap-1 hover:text-primary transition-colors">
                <Download className="w-3 h-3" /> iPhone CSV
              </a>
              <a href="/api/csv/android" className="flex items-center gap-1 hover:text-primary transition-colors">
                <Download className="w-3 h-3" /> Android CSV
              </a>
            </div>
          </div>
        </main>
      </div>
      <SiteFooter />
    </div>
  );
}
